import { searchClaimsQuery } from "@/api/claims";
import { pendingListingsQuery } from "@/api/listings";
import { useDismissMessageMutation } from "@/api/mgmtMessages";
import { eapUnclosedSessionsQuery, supplierUnrespondedMessagesQuery } from "@/api/reporting";
import { useHideSelfSignUpMutation } from "@/api/supplier";
import { surplusesQuery } from "@/api/supporter";
import { Permission, usePermissions } from "@/api/user";
import { api } from "@/api/utils";
import { DataTable, DataTableCard } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { StyledLink } from "@/components/link/StyledLink";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { IconButton } from "@/components/ui/IconButton";
import { FlexLink, IconButtonLink } from "@/components/ui/Link";
import { PagePending } from "@/components/ui/PagePending";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { describeSessionTime, isSessionOverdue, OverdueSessionPill } from "@/pages/admin/reports/eap/unclosedSessions";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/ToggleGroup";
import { formatTokensAsMoney, TokenCurrencyCode } from "@/modules/money-fns";
import { seo } from "@/utils/seo";
import { formatDate } from "@common/utils/date";
import { formatDollars } from "@common/utils/i18n";
import { Flex, Grid, QueryHandler, Span, Spinner, Text, toast } from "@givenwell/components";
import { CloseIcon, ExpandWindowIcon } from "@givenwell/core-solid-icons";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/admin/")({
  pendingComponent: PagePending,
  component: AdminHome,
  head: () => ({ meta: seo({ title: "" }) }),
});

function AdminHome() {
  const hasPermission = usePermissions();
  return (
    <PageWrapper title="Admin Overview">
      <Flex css={{ flexDirection: "column", items: "center", gap: 8 }}>
        <Grid
          css={{
            gridCols: 1,
            gap: 16,
            w: "100%",
            "@sm": {
              gridCols: 2,
            },
          }}
        >
          {hasPermission(Permission.Admin.ManageClaims) && <ClaimsToReview />}
          <ListingsToReview />
          <LowSupporters />
          <EapUnclosedSessions />
          <PartialSignUp />
          <Surpluses />
          {hasPermission(Permission.Admin.Dev) && <SupplierUnrespondedMessages />}
        </Grid>
      </Flex>
    </PageWrapper>
  );
}

function ClaimsToReview() {
  const pendingClaims = useQuery(
    searchClaimsQuery({
      page: 1,
      pageSize: 100,
      claimStatus: "pending",
    }),
  );

  return (
    <Card
      title="Claims to Review"
      actions={
        <IconButtonLink aria-label="See more" variant="plain" color="neutral" to="/admin/claims">
          <ExpandWindowIcon />
        </IconButtonLink>
      }
    >
      <QueryHandler
        query={pendingClaims}
        success={({ data }) => (
          <div>
            <Text css={{ color: colors.text2, mb: 8 }}>
              <Span css={{ weight: 500, color: colors.navy }}>{data.total_items}</Span> pending claims
            </Text>
            <div>
              {data.items.map(claim => (
                <Link
                  key={claim.claim_id}
                  to="/admin/claims/$claimId"
                  params={{
                    claimId: claim.claim_id,
                  }}
                >
                  <Flex css={{ items: "center", height: 36 }}>
                    <Text css={{ color: colors.text2 }}>
                      <Span
                        css={{ weight: 500, color: colors.navy, ":hover > * > &": { textDecoration: "underline" } }}
                      >
                        {claim.vendor_name}
                      </Span>
                    </Text>
                  </Flex>
                </Link>
              ))}
            </div>
          </div>
        )}
      />
    </Card>
  );
}

function ListingsToReview() {
  const pendingListings = useQuery(pendingListingsQuery(undefined, 1, 10));

  return (
    <Card
      title="Listings to Review"
      actions={
        <IconButtonLink aria-label="See more" variant="plain" color="neutral" to="/admin/listing-review">
          <ExpandWindowIcon />
        </IconButtonLink>
      }
    >
      <QueryHandler
        query={pendingListings}
        success={({ data }) => (
          <div>
            <Text css={{ color: colors.text2, mb: 8 }}>
              <Span css={{ weight: 500, color: colors.navy }}>{data.total_items}</Span> pending listings
            </Text>
            <div>
              {data.items.map(listing => (
                <Link
                  key={listing.id}
                  to="/admin/listing-review/$listingId/$version"
                  params={{
                    listingId: listing.id,
                    version: listing.version,
                  }}
                >
                  <Flex css={{ items: "center", height: 48 }}>
                    <Text css={{ color: colors.text2 }}>
                      <Span
                        css={{ weight: 500, color: colors.navy, ":hover > * > &": { textDecoration: "underline" } }}
                      >
                        {listing.title}
                      </Span>{" "}
                      by {listing.supplier_name} {listing.version === 1 && <Badge color="red">New</Badge>}{" "}
                      {listing.account_owner != null && <Badge color="green">{listing.account_owner}</Badge>}
                    </Text>
                  </Flex>
                </Link>
              ))}
            </div>
          </div>
        )}
      />
    </Card>
  );
}

function Surpluses() {
  const query = useQuery(surplusesQuery(1, 10, "unprocessed"));

  return (
    <Card
      title="Supporter Overpayments"
      actions={
        <IconButtonLink aria-label="See more" variant="plain" color="neutral" to="/admin/surpluses">
          <ExpandWindowIcon />
        </IconButtonLink>
      }
    >
      <QueryHandler
        query={query}
        success={({ data }) => (
          <div>
            <Text css={{ color: colors.text2, mb: 8 }}>
              <Span css={{ weight: 500, color: colors.navy }}>{data.total_items}</Span> unproccessed overpayments
            </Text>
            <div>
              {data.items.map(surplus => (
                <Flex key={surplus.surplus_id} css={{ items: "center", height: 48 }}>
                  <Text css={{ color: colors.text2 }}>
                    <Span css={{ weight: 500, color: colors.navy }}>
                      {surplus.surplus_amount}
                      {surplus.currency_code}
                    </Span>{" "}
                    by <Span css={{ weight: 500, color: colors.navy }}>{surplus.supporter_name}</Span>
                  </Text>
                </Flex>
              ))}
            </div>
          </div>
        )}
      />
    </Card>
  );
}

/**
 * EAP sessions that have taken place but which the provider has not closed off on their Session Review
 * screen. Payout waits on that review, so these need chasing.
 */
function EapUnclosedSessions() {
  const query = useQuery(eapUnclosedSessionsQuery());

  return (
    <Card
      title="Unclosed EAP Sessions"
      actions={
        <IconButtonLink
          aria-label="See more"
          variant="plain"
          color="neutral"
          to="/admin/reports/$countryCode/eap-unclosed-sessions"
          params={{ countryCode: "NZ" }}
        >
          <ExpandWindowIcon />
        </IconButtonLink>
      }
    >
      <QueryHandler
        query={query}
        success={({ data }) => (
          <div>
            <Text css={{ color: colors.text2, mb: 8 }}>
              <Span css={{ weight: 500, color: colors.navy }}>{data.length}</Span> unclosed sessions
            </Text>
            <div>
              {data.slice(0, 10).map(session => (
                <FlexLink
                  key={session.purchase_id}
                  to="/supplier/$supplierId/eap-review/$purchaseId"
                  params={{ supplierId: session.supplier_id, purchaseId: session.purchase_id }}
                  css={{ items: "center", height: 36, "&:hover": { backgroundColor: colors.warmGrey } }}
                >
                  <Text css={{ color: colors.text2, flex: "1 0 0px" }}>
                    <Span css={{ weight: 500, color: colors.navy }}>{session.supplier_name}</Span> session{" "}
                    {describeSessionTime(session.booking_start)}
                  </Text>
                  {isSessionOverdue(session.booking_start) && <OverdueSessionPill />}
                </FlexLink>
              ))}
            </div>
          </div>
        )}
      />
    </Card>
  );
}

function PartialSignUp() {
  const query = useQuery({
    queryKey: ["reports", "supplier-partial-self-sign-ups"],
    queryFn: () => api.reporting.getManagementReportingObsSupplierPartialSelfSignUps({ includeHidden: false }),
  });

  const hide = useHideSelfSignUpMutation();

  return (
    <Card
      title="Partial Sign Ups"
      actions={
        <IconButtonLink aria-label="See more" variant="plain" color="neutral" to="/admin/surpluses">
          <ExpandWindowIcon />
        </IconButtonLink>
      }
    >
      <QueryHandler
        query={query}
        success={({ data }) => (
          <div>
            <Text css={{ color: colors.text2, mb: 8 }}>
              <Span css={{ weight: 500, color: colors.navy }}>{data.length}</Span> partial sign ups
            </Text>
            <div>
              {data.map(signUp => (
                <Flex
                  key={signUp.sign_up_id}
                  css={{ items: "center", height: 48, "&:hover": { backgroundColor: colors.warmGrey } }}
                >
                  <Text css={{ color: colors.text2, flex: "1 0 0px" }}>
                    <Span css={{ weight: 500, color: colors.navy }}>{signUp.trading_name}</Span> by{" "}
                    <Span css={{ weight: 500, color: colors.navy }}>
                      {signUp.first_name} {signUp.last_name} ({signUp.email})
                    </Span>
                  </Text>
                  <IconButton
                    aria-label="Hide"
                    variant="plain"
                    color="neutral"
                    size="sm"
                    onClick={() => hide.mutate(signUp.sign_up_id)}
                  >
                    {hide.isPending ? <Spinner css={{ size: 24 }} /> : <CloseIcon />}
                  </IconButton>
                </Flex>
              ))}
            </div>
          </div>
        )}
      />
    </Card>
  );
}

type LowSupportersTab = "allowances" | "invoices";

function LowSupporters() {
  const [tab, setTab] = useState<LowSupportersTab>("allowances");

  return (
    <Card title="Low Supporters">
      <ToggleGroup
        type="single"
        value={tab}
        onValueChange={value => {
          if (value) setTab(value as LowSupportersTab);
        }}
        css={{ mb: 12 }}
      >
        <ToggleGroupItem value="allowances">Allowances</ToggleGroupItem>
        <ToggleGroupItem value="invoices">Overdue invoices</ToggleGroupItem>
      </ToggleGroup>
      {tab === "allowances" ? <UpcomingAllowancesTab /> : <OverdueInvoicesTab />}
    </Card>
  );
}

function UpcomingAllowancesTab() {
  const query = useQuery({
    queryKey: ["reporting", "supporter-upcoming-allowances"],
    queryFn: () => api.reporting.getManagementReportingObsSupporterUpcomingAllowances({ page: 1, pageSize: 1000 }),
  });
  const twentyDaysFromNow = new Date(new Date().getTime() + 20 * 24 * 60 * 60 * 1000);

  return (
    <QueryHandler
      query={query}
      success={({ data }) => {
        const now = new Date();
        const lowSupporters = data.items.filter(item => item.wallet_balance < item.tokens_total);
        const upcomingLowSupporters = lowSupporters.filter(
          item => item.next_date != null && new Date(item.next_date) < twentyDaysFromNow,
        );
        const actionRequired = upcomingLowSupporters.filter(item => {
          const invoiced = item.tokens_total - item.wallet_balance <= item.unpaid_invoice_tokens;
          if (!invoiced) return true;
          if (!item.unpaid_invoice_due_date) return false;
          return new Date(item.unpaid_invoice_due_date) < now;
        });
        return (
          <div>
            <Text css={{ color: colors.text2, mb: 8 }}>
              <Span css={{ weight: 500, color: colors.navy }}>{actionRequired.length}</Span> low supporters
            </Text>
            <div>
              {actionRequired.map(supporter => {
                const invoiced = supporter.tokens_total - supporter.wallet_balance <= supporter.unpaid_invoice_tokens;
                const overdue =
                  invoiced && !!supporter.unpaid_invoice_due_date && new Date(supporter.unpaid_invoice_due_date) < now;
                return (
                  <FlexLink
                    key={supporter.supporter_id}
                    to="/supporter/$supporterId"
                    params={{
                      supporterId: supporter.supporter_id,
                    }}
                    css={{ items: "center", height: 48, "&:hover": { backgroundColor: colors.warmGrey } }}
                  >
                    <Text css={{ color: colors.text2, flex: "1 0 0px" }}>
                      <Span css={{ weight: 500, color: colors.navy }}>{supporter.supporter}</Span> can't afford{" "}
                      {formatTokensAsMoney({
                        amount: supporter.tokens_total,
                        tokenCurrencyCode: supporter.token_currency_code as TokenCurrencyCode,
                      })}{" "}
                      for{" "}
                      {supporter.next_date
                        ? new Intl.DateTimeFormat("en-NZ", { dateStyle: "medium" }).format(
                            new Date(supporter.next_date),
                          )
                        : "an unknown date"}
                    </Text>

                    <Text
                      css={{
                        color: colors.white,
                        backgroundColor: colors.red500,
                        borderRadius: 8,
                        padding: 4,
                        fontSize: 9,
                      }}
                    >
                      {overdue ? "Invoice overdue" : "Missing invoice"}
                    </Text>
                  </FlexLink>
                );
              })}
            </div>
          </div>
        );
      }}
    />
  );
}

function OverdueInvoicesTab() {
  const query = useQuery({
    queryKey: ["reporting", "supporter-overdue-invoices"],
    queryFn: () => api.reporting.getManagementReportingObsSupporterOverdueInvoices(),
  });

  return (
    <QueryHandler
      query={query}
      success={({ data }) => (
        <div>
          <Text css={{ color: colors.text2, mb: 8 }}>
            <Span css={{ weight: 500, color: colors.navy }}>{data.length}</Span> supporters with overdue invoices
          </Text>
          <div>
            {data.map(supporter => (
              <FlexLink
                key={supporter.supporter_id}
                to="/supporter/$supporterId"
                params={{
                  supporterId: supporter.supporter_id,
                }}
                css={{ items: "center", height: 48, "&:hover": { backgroundColor: colors.warmGrey } }}
              >
                <Text css={{ color: colors.text2, flex: "1 0 0px" }}>
                  <Span css={{ weight: 500, color: colors.navy }}>{supporter.supporter}</Span> owes{" "}
                  {formatDollars(supporter.total_outstanding, supporter.currency_code)} across {supporter.invoice_count}{" "}
                  {supporter.invoice_count === 1 ? "invoice" : "invoices"} — oldest due{" "}
                  {new Intl.DateTimeFormat("en-NZ", { dateStyle: "medium" }).format(
                    new Date(supporter.oldest_due_date),
                  )}
                </Text>

                <Text
                  css={{
                    color: colors.white,
                    backgroundColor: colors.red500,
                    borderRadius: 8,
                    padding: 4,
                    fontSize: 9,
                  }}
                >
                  Overdue
                </Text>
              </FlexLink>
            ))}
          </div>
        </div>
      )}
    />
  );
}

function SupplierUnrespondedMessages() {
  const supporterDataQuery = useQuery(supplierUnrespondedMessagesQuery());
  const dismissMut = useDismissMessageMutation();

  function handleDismissMessage(messageId: string) {
    dismissMut.mutate(messageId, {
      onSuccess: () => toast.success("Message dismissed"),
    });
  }

  return (
    <DataTable css={{ overflow: "hidden", gridColumn: "1 / -1" }}>
      <DataTableCard>
        <Text css={{ fontScale: "lg", weight: 500 }}>Unresponded messages</Text>
      </DataTableCard>
      <DataTableBody
        data={supporterDataQuery.data ?? []}
        status={supporterDataQuery.status}
        columns={[
          {
            header: "Supplier",
            cell: d => (
              <StyledLink
                to="/supplier/$supplierId/messages/$conversationId"
                params={{ conversationId: d.conversation_id, supplierId: d.supplier_id }}
              >
                {d.supplier_name}
              </StyledLink>
            ),
            css: { verticalAlign: "top" },
          },
          {
            header: "Date",
            cell: d => formatDate(d.message_time),
            css: { verticalAlign: "top" },
          },
          {
            header: "Member",
            cell: d => (
              <StyledLink to="/admin/members/$memberId" params={{ memberId: d.member_id }}>
                {d.member_name}
              </StyledLink>
            ),
            css: { verticalAlign: "top" },
          },
          {
            header: "Message",
            cell: "message_text",
            css: { maxW: 500, whiteSpace: "normal", verticalAlign: "top" },
          },
          {
            header: "",
            cell: d => (
              <>
                <Button variant="tonal" size="xs" onClick={() => handleDismissMessage(d.message_id)}>
                  Dismiss
                </Button>
              </>
            ),
          },
        ]}
        dataKey="conversation_id"
      />
    </DataTable>
  );
}
